import { Routes, Route, Navigate } from 'react-router-dom'
import { lazy, Suspense, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { getSession } from './store/slices/authSlice'
import { removeToast } from './store/slices/toastSlice'
import ProtectedRoute from './components/ProtectedRoute'
import GuestCheckoutRoute from './components/GuestCheckoutRoute'
import AdminRoute from './components/AdminRoute'
import Unauthorized from './pages/Unauthorized'
import MaintenanceMode from './components/MaintenanceMode'
import { ToastContainer } from './components/Toast'
import { Provider } from 'react-redux'
import store from './store'
import PayPalProvider from './components/PayPalProvider'

// Layouts
const MainLayout = lazy(() => import('./layouts/MainLayout'))
const AdminLayout = lazy(() => import('./pages/admin/AdminLayout'))

// Pages
const Home = lazy(() => import('./pages/Home'))
const ProductList = lazy(() => import('./pages/ProductList'))
const ProductDetail = lazy(() => import('./pages/ProductDetail'))
const Cart = lazy(() => import('./pages/Cart'))
const Checkout = lazy(() => import('./pages/Checkout'))
const NotFound = lazy(() => import('./pages/NotFound'))
const Login = lazy(() => import('./pages/Login'))
const Register = lazy(() => import('./pages/Register'))
const Profile = lazy(() => import('./pages/Profile'))
const Products = lazy(() => import('./pages/Products'))
const Product = lazy(() => import('./pages/Product'))
const Debug = lazy(() => import('./pages/Debug'))
const OrderConfirmation = lazy(() => import('./pages/OrderConfirmation'))
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'))
const TermsOfService = lazy(() => import('./pages/TermsOfService'))
const OrderSuccess = lazy(() => import('./pages/OrderSuccess'))
const SizeChart = lazy(() => import('./pages/SizeChart'))
const About = lazy(() => import('./pages/About'))
const AuthCallback = lazy(() => import('./pages/AuthCallback'))

// Admin Pages
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'))
const AdminProductList = lazy(() => import('./pages/admin/AdminProductList'))
const AdminProductForm = lazy(() => import('./pages/admin/AdminProductForm'))
const AdminOrders = lazy(() => import('./pages/admin/AdminOrders'))
const OrderDetails = lazy(() => import('./pages/admin/OrderDetails'))
const AdminUsers = lazy(() => import('./pages/admin/AdminUsers'))
const AdminLeagues = lazy(() => import('./pages/admin/AdminLeagues'))
const AdminSettings = lazy(() => import('./pages/admin/AdminSettings'))
const CreateAdmin = lazy(() => import('./pages/admin/CreateAdmin'))
const AdminLeagueForm = lazy(() => import('./pages/admin/AdminLeagueForm'))

function App() {
  const dispatch = useDispatch();
  const toasts = useSelector(state => state.toast.toasts);

  // Check for existing session when app loads
  useEffect(() => {
    dispatch(getSession());
  }, [dispatch]);

  return (
    <Provider store={store}>
      <PayPalProvider>
        <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 text-slate-900 dark:text-white">
          <MaintenanceMode>
            <Suspense fallback={
              <div className="flex items-center justify-center min-h-screen">
                <div className="flex flex-col items-center">
                  <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-indigo-600 dark:border-indigo-400"></div>
                  <p className="mt-4 text-indigo-600 dark:text-indigo-400 font-medium">Loading amazing content...</p>
                </div>
              </div>
            }>
              <Routes>
                {/* Main Routes */}
                <Route path="/" element={<MainLayout />}>
                  <Route index element={<Home />} />
                  <Route path="products">
                    <Route index element={<Products />} />
                    <Route path=":league" element={<ProductList />} />
                    <Route path=":league/:id" element={<ProductDetail />} />
                    <Route path=":id" element={<Product />} />
                  </Route>
                  <Route path="cart" element={<Cart />} />
                  <Route path="login" element={<Login />} />
                  <Route path="register" element={<Register />} />
                  <Route path="unauthorized" element={<Unauthorized />} />
                  <Route path="debug" element={<Debug />} />
                  <Route path="privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="terms-of-service" element={<TermsOfService />} />
                  <Route path="size-chart" element={<SizeChart />} />
                  <Route path="about" element={<About />} />
                  <Route path="order-success" element={<OrderSuccess />} />
                  <Route path="auth/callback" element={<AuthCallback />} />
                  
                  {/* Protected routes */}
                  <Route 
                    path="profile" 
                    element={
                      <ProtectedRoute>
                        <Profile />
                      </ProtectedRoute>
                    }
                  />
                  <Route 
                    path="checkout" 
                    element={
                      <GuestCheckoutRoute>
                        <Checkout />
                      </GuestCheckoutRoute>
                    }
                  />
                  <Route path="order-confirmation/:orderId" element={
                    <Suspense fallback={
                      <div className="flex items-center justify-center min-h-[400px]">
                        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600 dark:border-indigo-400"></div>
                      </div>
                    }>
                      <OrderConfirmation />
                    </Suspense>
                  } />
                  <Route path="*" element={<NotFound />} />
                </Route>
                
                {/* Admin routes */}
                <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
                  <Route index element={<AdminDashboard />} />
                  <Route path="products" element={<AdminProductList />} />
                  <Route path="products/new" element={<AdminProductForm />} />
                  <Route path="products/edit/:id" element={<AdminProductForm />} />
                  <Route path="orders" element={<AdminOrders />} />
                  <Route path="orders/:id" element={<OrderDetails />} />
                  <Route path="users" element={<AdminUsers />} />
                  <Route path="leagues" element={<AdminLeagues />} />
                  <Route path="leagues/add" element={<AdminLeagueForm />} />
                  <Route path="leagues/edit/:id" element={<AdminLeagueForm />} />
                  <Route path="settings" element={<AdminSettings />} />
                </Route>
                
                {/* Create admin page - not under admin layout since it's for initial setup */}
                <Route path="/create-admin" element={<CreateAdmin />} />
                
                {/* Catch-all */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
          </MaintenanceMode>
          <ToastContainer 
            toasts={toasts} 
            removeToast={(id) => dispatch(removeToast(id))} 
          />
        </div>
      </PayPalProvider>
    </Provider>
  )
}

export default App